import { useEffect } from 'react';

import { Analytics, formatContainers } from '@trello/atlassian-analytics';
import { getMemberId } from '@trello/authentication';
import { checkIsTemplate } from '@trello/business-logic/board';
import {
  customFieldsId as CUSTOM_FIELDS_POWER_UP_ID,
  mapPowerUpId as MAP_POWER_UP_ID,
} from '@trello/config';
import { PremiumFeatures } from '@trello/entitlements';
import { useFeatureGate } from '@trello/feature-gate-client';
import { client } from '@trello/graphql';
import { showLabelsState } from '@trello/labels/showLabelsState';
import { getScreenFromUrl } from '@trello/marketing-screens';
import { splitScreenSharedState } from '@trello/split-screen';
import { getGlobalTheme } from '@trello/theme';
import { workspaceNavigationState } from '@trello/workspace-navigation';

// eslint-disable-next-line no-restricted-imports
import { Controller } from 'app/scripts/controller';
import { BUTLER_POWER_UP_ID } from 'app/scripts/data/butler-id';
import { collapsedListsState } from 'app/src/components/CollapsedListsState';
import { DEFAULT_LIST_COLOR } from 'app/src/components/ListColorPicker/ListColor.constants';
import { getMarketingScreenInfo } from 'app/src/getMarketingScreenInfo';
import type {
  BoardMarketingAnalyticDataQuery,
  BoardMarketingAnalyticDataQueryVariables,
} from './BoardMarketingAnalyticDataQuery.generated';
import { BoardMarketingAnalyticDataDocument } from './BoardMarketingAnalyticDataQuery.generated';
import { boardSidebarState } from './boardSidebarState';

export const useBoardMarketingScreenAnalytic = (boardId: string) => {
  const { value: isSplitScreenEnabled } = useFeatureGate(
    'trello_personal_productivity_release',
  );

  useEffect(() => {
    /**
     * Use an idle callback to avoid any expensive logic for watching queries
     * or other attributes taking time while we render the board. This isn't high
     * priority, so we can defer it.
     */
    const idleCallbackId = window.requestIdleCallback(() => {
      const data = client.readQuery<
        BoardMarketingAnalyticDataQuery,
        BoardMarketingAnalyticDataQueryVariables
      >({
        query: BoardMarketingAnalyticDataDocument,
        variables: {
          memberId: getMemberId() ?? '', // can be null on a public board
          boardId,
        },
      });

      const board = data?.board;
      const member = data?.member;

      // Analytics migration -- move this to controller to capture all URL changes
      const { url, referrerUrl, referrerScreenName } = getMarketingScreenInfo();
      const { colorMode, effectiveColorMode } = getGlobalTheme();

      const isCollapsibleListsEnabled = board?.premiumFeatures?.includes(
        PremiumFeatures.collapsibleLists,
      );
      const hasCollapsedLists = isCollapsibleListsEnabled
        ? Object.values(collapsedListsState.value[boardId] || {}).some(Boolean)
        : undefined;

      const isListColorsEnabled = board?.premiumFeatures?.includes(
        PremiumFeatures.listColors,
      );
      const hasColoredLists = isListColorsEnabled
        ? board?.lists?.some((list) =>
            Boolean(list.color && list.color !== DEFAULT_LIST_COLOR),
          )
        : undefined;

      const numOpenCardsPerListMap = Object.fromEntries(
        board?.lists?.map((list) => [list.id, 0]) ?? [],
      );
      board?.cards?.forEach((card) => {
        numOpenCardsPerListMap[card.idList] =
          numOpenCardsPerListMap[card.idList] + 1;
      });
      // Convert the map into an array, maintaining the correct order of the lists
      const numOpenCardsPerList = board?.lists?.map(
        (list) => numOpenCardsPerListMap[list.id],
      );

      // Matches the logic of the powerUpsCount() method in app/scripts/models/Board.ts
      const numPowerups = (board?.boardPlugins ?? []).filter(
        (plugin) =>
          plugin.idPlugin !== BUTLER_POWER_UP_ID &&
          plugin.idPlugin !== MAP_POWER_UP_ID &&
          plugin.idPlugin !== CUSTOM_FIELDS_POWER_UP_ID,
      ).length;

      const numOpenMirrorCards = board?.cards?.filter(
        ({ cardRole }) => cardRole === 'mirror',
      ).length;

      const numDoneCards = board?.cards?.filter(
        ({ dueComplete }) => dueComplete === true,
      ).length;

      const mirrorCardState = board?.myPrefs?.showCompactMirrorCards
        ? 'compact'
        : 'expanded';

      // We only want to include these attributes when SplitScreen is enabled
      const splitScreenState = isSplitScreenEnabled
        ? {
            isBoardOpen: splitScreenSharedState.value.board,
            isInboxOpen: splitScreenSharedState.value.inbox,
            isPlannerOpen: splitScreenSharedState.value.planner,
          }
        : {};

      Analytics.sendMarketingScreenEvent({
        url,
        referrerUrl,
        referrerScreenName,
        event: {
          source: 'boardScreen',
          name: 'boardScreenV2',
          attributes: {
            colorMode,
            effectiveColorMode,
            areCardCoversDisabled: board?.prefs?.cardCovers === false,
            areKeyboardShortcutsEnabled:
              member?.prefs?.keyboardShortcutsEnabled ?? false,
            hasCollapsedLists,
            hasColoredLists,
            isBoardCanvasModernizationEnabled: true,
            isColorBlindModeEnabled: member?.prefs?.colorBlind ?? false,
            isFirstTimeViewingBoard: Controller.isFirstTimeViewingBoard,
            isShowingSidebar: boardSidebarState.value,
            isShowingWorkspaceNavigation:
              workspaceNavigationState.value.expanded,
            isTemplate: board && checkIsTemplate(board),
            labelsState: showLabelsState.value.showText
              ? 'expanded'
              : 'collapsed',
            mirrorCardState,
            ...splitScreenState,
            numDoneCards,
            numOpenLists: board?.lists?.length,
            numOpenCards: board?.cards?.length,
            numOpenCardsPerList,
            numOpenMirrorCards,
            numPowerups,
            screen: getScreenFromUrl(),
            viewerIsBoardCreator: board?.idMemberCreator === getMemberId(),
            visibility: board?.prefs?.permissionLevel,
          },
          containers: formatContainers({
            idBoard: board?.id,
            idOrganization: board?.idOrganization,
          }),
        },
      });
    });

    return () => {
      window.cancelIdleCallback(idleCallbackId);
    };
    // We only want this to run once when the boardId changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boardId]);
};
